import React, { useState, useEffect, useRef } from 'react';
import { connect } from 'react-redux';
import { Navbar, Nav, Dropdown } from "react-bootstrap";
import { Link } from 'react-router-dom';
import Logo from '../common/Logo';
import TenantSelector from '../common/TenantSelector';
import ImpersonationBanner from '../common/ImpersonationBanner';
import TokenDisplay from '../common/TokenDisplay';

const HeaderLoggedInMobile = ({auth}) => {
    const [expanded, setExpanded] = useState(false);
    const [showDropdown, setShowDropdown] = useState(false);
    const [tenantRole, setTenantRole] = useState(null);
    const [tenantCount, setTenantCount] = useState(0);
    const dropdownRef = useRef(null);

    // Close dropdown on outside click
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setShowDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Fetch tenant role and count for current user
    useEffect(() => {
        const fetchTenantInfo = async () => {
            if (!auth?.id) return;
            try {
                const response = await fetch('/api/user/tenants', { credentials: 'include' });
                if (response.ok) {
                    const data = await response.json();
                    const activeTenant = data.tenants?.find(t => t.id === data.activeTenantId);
                    setTenantRole(activeTenant?.role || null);
                    setTenantCount(data.tenants?.length || 0);
                }
            } catch (err) {
                console.error('Error fetching tenant info:', err);
            }
        };
        fetchTenantInfo();
    }, [auth?.id]);

    const userInitial = auth?.first_name ? auth.first_name.charAt(0).toUpperCase() : null;

    // Loading state navbar
    const navbarStyle = {
        background: 'rgba(255, 255, 255, 0.98)',
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        backdropFilter: 'blur(10px)'
    };

    // Don't render if auth data isn't loaded yet
    if (!auth || !auth.id) {
        return (
            <div className="d-block d-lg-none">
                <Navbar fixed="top" expand="md" className="navbar-custom" style={navbarStyle}>
                    <Navbar.Brand className="p-0" style={{marginLeft:"10px", marginRight:"10px"}}>
                        <Link to="/dashboard" style={{color: "#1a1a2e", textDecoration:"none", display: 'flex', alignItems: 'center'}}>
                            <Logo size={24} color="#6366f1" showText textColor="#1a1a2e" />
                        </Link>
                    </Navbar.Brand>
                </Navbar>
            </div>
        );
    }

    const isAdmin = auth.platform_role === 'super_admin' || auth.platform_role === 'admin';
    const isTenantAdmin = tenantRole === 'admin' || tenantRole === 'manager';
    const hasMultipleTenants = tenantCount > 1;

    return (
        <>
        <ImpersonationBanner />
        <div className="d-block d-lg-none">
            <Navbar
                fixed="top"
                expanded={expanded}
                onToggle={() => setExpanded(!expanded)}
                expand="md"
                className="navbar-custom"
                style={navbarStyle}
            >
                <Navbar.Brand className="p-0" style={{marginLeft:"10px", marginRight:"10px"}}>
                    <Link to="/dashboard" style={{color: "#1a1a2e", textDecoration:"none", display: 'flex', alignItems: 'center'}}>
                        <Logo size={24} color="#6366f1" showText textColor="#1a1a2e" />
                    </Link>
                </Navbar.Brand>

                {/* User avatar - always visible */}
                <div className="d-flex align-items-center ms-auto" style={{ marginRight: '10px' }}>
                    <div ref={dropdownRef}>
                        <Dropdown show={showDropdown} onToggle={() => {}}>
                            <div
                                onClick={() => setShowDropdown(!showDropdown)}
                                style={{
                                    width: '35px',
                                    height: '35px',
                                    borderRadius: '50%',
                                    backgroundColor: '#6366f1',
                                    color: 'white',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    cursor: 'pointer',
                                    userSelect: 'none',
                                    fontSize: '0.9em',
                                }}
                            >
                                {userInitial === null ? <i className="bi bi-person-fill fs-5"></i> : userInitial}
                            </div>

                            <Dropdown.Menu align="end" show={showDropdown} className="custom-dropdown-menu p-0" style={{minWidth:"200px"}}>
                                <Dropdown.Item
                                    as={Link}
                                    to="/dashboard/settings"
                                    className="py-2"
                                    onClick={() => setShowDropdown(false)}
                                    style={{ textDecoration: 'none' }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/>
                                        <circle cx="12" cy="7" r="4"/>
                                    </svg>
                                    Profile Settings
                                </Dropdown.Item>

                                {/* Org Admin - only for tenant admins/managers */}
                                {isTenantAdmin && (
                                    <Dropdown.Item
                                        as={Link}
                                        to="/dashboard/settings#organization-settings"
                                        className="py-2"
                                        onClick={() => setShowDropdown(false)}
                                        style={{ textDecoration: 'none' }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                            <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                                            <circle cx="9" cy="7" r="4"/>
                                            <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                                            <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                                        </svg>
                                        Org Admin
                                    </Dropdown.Item>
                                )}

                                {/* Platform - for super admins/admins */}
                                {isAdmin && (
                                    <Dropdown.Item
                                        as={Link}
                                        to="/admin/tenants"
                                        className="py-2"
                                        onClick={() => setShowDropdown(false)}
                                        style={{ textDecoration: 'none', color: '#6366f1' }}
                                    >
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                                        </svg>
                                        Platform Admin
                                    </Dropdown.Item>
                                )}

                                <Dropdown.Divider style={{ margin: '0' }} />
                                <Dropdown.Item
                                    as={Link}
                                    to="/logout"
                                    className="py-2"
                                    onClick={() => setShowDropdown(false)}
                                    style={{ color:"#dc3545", textDecoration: 'none' }}
                                >
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: '0.4rem' }}>
                                        <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                                        <polyline points="16 17 21 12 16 7"/>
                                        <line x1="21" y1="12" x2="9" y2="12"/>
                                    </svg>
                                    Logout
                                </Dropdown.Item>
                            </Dropdown.Menu>
                        </Dropdown>
                    </div>
                </div>

                <Navbar.Toggle
                    aria-controls="mobile-nav-loggedin"
                    onClick={() => setExpanded(!expanded)}
                    style={{
                        border: 'none',
                        padding: '0.5rem',
                        marginRight: '10px'
                    }}
                />

                <Navbar.Collapse id="mobile-nav-loggedin">
                    <Nav className="ms-auto" style={{ padding: '0.5rem 0' }}>
                        {/* Tenant & Tokens - moved here from top bar */}
                        <div style={{ padding: '0.5rem 1rem', display: 'flex', alignItems: 'center', gap: '0.75rem', borderBottom: '1px solid #f1f5f9', marginBottom: '0.25rem' }}>
                            {hasMultipleTenants && <TenantSelector />}
                            <TokenDisplay />
                        </div>
                        {/* Dashboard - always visible */}
                        <Link
                            to="/dashboard"
                            onClick={() => setExpanded(false)}
                            className="d-flex align-items-center"
                            style={{
                                color: "#4a5568",
                                textDecoration: "none",
                                fontSize: "1rem",
                                fontWeight: "400",
                                padding: "0.75rem 1rem",
                                borderRadius: "0.25rem"
                            }}
                        >
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginRight: "0.5rem" }}>
                                <rect x="3" y="3" width="7" height="7"/>
                                <rect x="14" y="3" width="7" height="7"/>
                                <rect x="14" y="14" width="7" height="7"/>
                                <rect x="3" y="14" width="7" height="7"/>
                            </svg>
                            Dashboard
                        </Link>
                        <Link
                            to="/contact"
                            onClick={() => setExpanded(false)}
                            className="d-flex align-items-center"
                            style={{
                                color: "#4a5568",
                                textDecoration: "none",
                                fontSize: "1rem",
                                fontWeight: "400",
                                padding: "0.75rem 1rem",
                                borderRadius: "0.25rem"
                            }}
                        >
                            Contact
                        </Link>
                    </Nav>
                </Navbar.Collapse>
            </Navbar>
        </div>
        </>
    )
}

function mapStateToProps({auth}) {
    return { auth };
}

export default connect(mapStateToProps)(HeaderLoggedInMobile);
